// src/features/staff/components/UploadProfilePictureDialog.jsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useProfilePictureUpload } from '../../files/hooks/useProfilePictureUpload';
import { FileDropZone, useFilePicker } from '../../files/upload';
import { formatFileSize } from '../../files/utils/formatFileSize';

export default function UploadProfilePictureDialog({ 
  open, 
  onClose, 
  staffMember,
  onUploadSuccess 
}) {
  const { t } = useTranslation(['staff', 'common', 'files']);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [validationError, setValidationError] = useState('');

  const { loading, error, uploadProfilePicture, clearError } = useProfilePictureUpload();

  // From the picker or dropped on the picture; shown as a preview before upload
  const handleFileSelect = useCallback(([file]) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setValidationError(t('staff:profile_picture.invalid_file_type'));
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setValidationError(t('staff:profile_picture.file_too_large'));
      return;
    }

    setSelectedFile(file);
    setValidationError('');
    clearError();

    // Create preview URL
    setPreviewUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return URL.createObjectURL(file);
    });
  }, [t, clearError]);
  const picker = useFilePicker({ onFiles: handleFileSelect, accept: 'image/*', multiple: false });

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !staffMember) return;

    const result = await uploadProfilePicture(selectedFile, staffMember.staff_member_id);

    if (result.success) {
      onUploadSuccess?.(result.data);
      handleClose();
    }
    // Error is handled by the hook and displayed via the error state
  }, [selectedFile, staffMember, uploadProfilePicture, onUploadSuccess]);

  const handleClose = useCallback(() => {
    if (!loading) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setValidationError('');
      clearError();
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      onClose();
    }
  }, [loading, previewUrl, onClose, clearError]);

  // Combined error message
  const displayError = validationError || error;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {t('staff:profile_picture.upload_title', { name: staffMember?.name })}
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
          {/* Preview; a picture can be dropped on it */}
          <FileDropZone
            onFiles={handleFileSelect}
            multiple={false}
            disabled={loading}
            label={t('files:upload.drop_file')}
            sx={{ display: 'flex', justifyContent: 'center', mb: 2, p: 2, borderRadius: 2 }}
          >
            {previewUrl ? (
              <Avatar
                src={previewUrl}
                sx={{ width: 150, height: 150 }}
              />
            ) : (
              <Avatar sx={{ width: 150, height: 150, bgcolor: 'primary.main' }}>
                <PersonIcon sx={{ fontSize: 80 }} />
              </Avatar>
            )}
          </FileDropZone>

          {/* File Input */}
          <Box width="100%">
            {picker.input}
            <Button
              variant="outlined"
              fullWidth
              startIcon={<CloudUploadIcon />}
              disabled={loading}
              onClick={picker.open}
            >
              {selectedFile
                ? t('staff:profile_picture.change_file')
                : t('staff:profile_picture.select_file')
              }
            </Button>
            {!selectedFile && (
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={1}>
                {t('files:upload.drop_on_picture')}
              </Typography>
            )}
          </Box>

          {/* Selected File Info */}
          {selectedFile && (
            <Box width="100%">
              <Typography variant="body2" color="text.secondary">
                {t('staff:profile_picture.selected_file')}: {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('staff:profile_picture.file_size')}: {formatFileSize(selectedFile.size)}
              </Typography>
            </Box>
          )}

          {/* Error Alert */}
          {displayError && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {displayError}
            </Alert>
          )}

          {/* Info Alert */}
          <Alert severity="info" sx={{ width: '100%' }}>
            <Typography variant="body2">
              {t('staff:profile_picture.info_formats')}
            </Typography>
            <Typography variant="caption" display="block" mt={1}>
              {t('staff:profile_picture.info_folder', { 
                defaultValue: 'Files will be stored in the "Profile Pictures" folder' 
              })}
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
        >
          {loading 
            ? t('staff:profile_picture.uploading') 
            : t('staff:profile_picture.upload')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}